import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { Box, CircularProgress, FormHelperText, Typography, useTheme } from "@mui/material";
import type {
  EditorThemeClasses,
  HTMLConfig,
  InitialEditorStateType,
  Klass,
  LexicalEditor,
  LexicalNode,
  LexicalNodeReplacement,
} from "lexical";
import type { FC, ReactElement } from "react";
import { useEffect, useState } from "react";
import { useLexicalContext } from "./lexical-context";
import type { LexicalEditorProps } from "./lexical-editor";
import { EDITOR_THEME } from "./theme/theme";
import "./theme/theme.css";
import type { SerialisedEditorDesign } from "./types/serialised-editor-design";
import { loadDesign } from "./utils/load-design";
import { EDITOR_NODES } from "./utils/nodes";

type LexicalEditorContainerProps = Pick<LexicalEditorProps, "format"> & {
  disabled?: boolean;
  error?: string;
  keyID: string;
  design?: SerialisedEditorDesign;
  nodes?: ReadonlyArray<Klass<LexicalNode> | LexicalNodeReplacement>;
  onError: (e: Error, editor: LexicalEditor) => void;
  children: ReactElement;
};

export const LexicalEditorContainer: FC<LexicalEditorContainerProps> = ({
  disabled,
  format,
  design,
  error,
  keyID,
  nodes,
  onError,
  children,
}) => {
  const muiTheme = useTheme();
  const {
    focus: { focused },
    drag: { draggedOver },
    upload: { uploading },
  } = useLexicalContext();
  const [initialConfig, setInitialConfig] = useState<Readonly<{
    namespace: string;
    nodes?: readonly (Klass<LexicalNode> | LexicalNodeReplacement)[];
    onError: (error: Error, editor: LexicalEditor) => void;
    onWarn?: (error: Error, editor: LexicalEditor) => void;
    editable?: boolean;
    theme?: EditorThemeClasses;
    editorState?: InitialEditorStateType;
    html?: HTMLConfig;
  }> | null>(null);
  const nodesToUse = nodes ?? EDITOR_NODES[format];

  // Clear initialConfig so a new keyID remounts with non-stale config
  const [previousKeyID, setPreviousKeyID] = useState(keyID);
  if (previousKeyID !== keyID) {
    setPreviousKeyID(keyID);
    if (initialConfig) setInitialConfig(null);
  }

  useEffect(() => {
    loadDesign(design, format)
      .then((editorState) => {
        setInitialConfig({
          editorState,
          editable: !disabled,
          namespace: "ce",
          onError,
          nodes: nodesToUse,
          theme: EDITOR_THEME,
        });
      })
      .catch(console.error);
  }, [design, format, disabled, nodesToUse, onError, keyID]);

  if (!initialConfig) return <CircularProgress />;
  return (
    <>
      <Box
        className={`editor-container ${muiTheme.palette.mode}-mode ${format}-format`}
        sx={{
          position: "relative",
          border: 1,
          borderRadius: 1,
          borderStyle: "solid",
          borderColor: draggedOver ? "secondary.main" : "divider",
          background: (theme) =>
            focused
              ? `linear-gradient(${theme.palette.primary.main},${theme.palette.primary.main}) bottom/100% 5px no-repeat;`
              : "",
        }}
      >
        {uploading && <UploadingBox />}
        <LexicalComposer key={keyID} initialConfig={initialConfig}>
          {children}
        </LexicalComposer>
        {error && (
          <FormHelperText error variant="filled">
            {error}
          </FormHelperText>
        )}
      </Box>
    </>
  );
};

const UploadingBox: FC = () => {
  return (
    <Box
      sx={{
        position: "absolute",
        top: "5%",
        left: "2%",
        width: "96%",
        height: "92%",
        zIndex: 100,
        backgroundColor: "white",
        opacity: "0.8",
        borderRadius: "5px",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "text.disabled",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        justifyContent: "center",
        alignItems: "center",
        color: "text.secondary",
      }}
    >
      <CircularProgress />
      <Typography>Inserting...</Typography>
    </Box>
  );
};
